import { supabase } from '../../lib/supabase';
import { formatCommunicationGap, getDaysSince } from '../../utils/date-formatting';
import { salesActivitiesService } from './activities.service';
export class SalesContactsService {
    /**
     * Get all contacts for an organization with optional filtering
     */
    async getContacts(organizationId, filters) {
        try {
            let query = supabase
                .from('sales_contacts')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('is_active', true)
                .order('created_at', { ascending: false });
            // Apply filters
            if (filters?.search) {
                query = query.textSearch('search_vector', filters.search);
            }
            if (filters?.stage && filters.stage.length > 0) {
                query = query.in('stage', filters.stage);
            }
            if (filters?.tags && filters.tags.length > 0) {
                query = query.contains('tags', filters.tags);
            }
            if (filters?.ownerId) {
                query = query.eq('owner_id', filters.ownerId);
            }
            if (filters?.isStarred !== undefined) {
                query = query.eq('is_starred', filters.isStarred);
            }
            if (filters?.dateRange) {
                query = query
                    .gte('created_at', filters.dateRange.start.toISOString())
                    .lte('created_at', filters.dateRange.end.toISOString());
            }
            const { data, error } = await query;
            if (error) {
                console.error('Error fetching contacts:', error);
                throw error;
            }
            return this.transformContacts(data || []);
        }
        catch (error) {
            console.error('Error in getContacts:', error);
            return [];
        }
    }
    /**
     * Get a single contact by ID with related data
     */
    async getContact(contactId) {
        try {
            const { data: contact, error: contactError } = await supabase
                .from('sales_contacts')
                .select('*')
                .eq('id', contactId)
                .single();
            if (contactError) {
                console.error('Error fetching contact:', contactError);
                return null;
            }
            // Fetch related deals
            const { data: deals, error: dealsError } = await supabase
                .from('sales_deals')
                .select('*')
                .eq('contact_id', contactId)
                .order('created_at', { ascending: false });
            // Fetch related activities
            const { data: activities, error: activitiesError } = await supabase
                .from('sales_activities')
                .select('*')
                .eq('contact_id', contactId)
                .order('created_at', { ascending: false })
                .limit(50);
            // Fetch follow-ups
            const { data: followUps, error: followUpsError } = await supabase
                .from('sales_follow_ups')
                .select('*')
                .eq('contact_id', contactId)
                .eq('status', 'pending')
                .order('due_date', { ascending: true });
            return {
                ...this.transformContact(contact),
                deals: deals || [],
                activities: activities || [],
                followUps: followUps || []
            };
        }
        catch (error) {
            console.error('Error in getContact:', error);
            return null;
        }
    }
    /**
     * Create a new contact
     */
    async createContact(organizationId, userId, request) {
        try {
            const { data, error } = await supabase
                .from('sales_contacts')
                .insert({
                organization_id: organizationId,
                full_name: request.fullName,
                email: request.email,
                phone: request.phone,
                company_name: request.companyName,
                contact_type: request.contactType,
                stage: request.stage,
                tags: request.tags || [],
                notes: request.notes,
                created_by: userId,
                owner_id: userId,
                assigned_to: userId
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating contact:', error);
                throw error;
            }
            return this.transformContact(data);
        }
        catch (error) {
            console.error('Error in createContact:', error);
            return null;
        }
    }
    /**
     * Update an existing contact
     */
    async updateContact(request) {
        try {
            const { id, ...updates } = request;
            const { data, error } = await supabase
                .from('sales_contacts')
                .update({
                full_name: updates.fullName,
                email: updates.email,
                phone: updates.phone,
                company_name: updates.companyName,
                contact_type: updates.contactType,
                stage: updates.stage,
                tags: updates.tags,
                notes: updates.notes
            })
                .eq('id', id)
                .select()
                .single();
            if (error) {
                console.error('Error updating contact:', error);
                throw error;
            }
            return this.transformContact(data);
        }
        catch (error) {
            console.error('Error in updateContact:', error);
            return null;
        }
    }
    /**
     * Delete a contact (soft delete)
     */
    async deleteContact(contactId) {
        try {
            const { error } = await supabase
                .from('sales_contacts')
                .update({ is_active: false })
                .eq('id', contactId);
            if (error) {
                console.error('Error deleting contact:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in deleteContact:', error);
            return false;
        }
    }
    /**
     * Toggle star status
     */
    async toggleStar(contactId, isStarred) {
        try {
            const { error } = await supabase
                .from('sales_contacts')
                .update({ is_starred: isStarred })
                .eq('id', contactId);
            if (error) {
                console.error('Error toggling star:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in toggleStar:', error);
            return false;
        }
    }
    /**
     * Update contact tags
     */
    async updateTags(contactId, tags) {
        try {
            const { error } = await supabase
                .from('sales_contacts')
                .update({ tags })
                .eq('id', contactId);
            if (error) {
                console.error('Error updating tags:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateTags:', error);
            return false;
        }
    }
    /**
     * Update contact stage
     */
    async updateStage(contactId, stage) {
        try {
            const { error } = await supabase
                .from('sales_contacts')
                .update({ stage })
                .eq('id', contactId);
            if (error) {
                console.error('Error updating stage:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateStage:', error);
            return false;
        }
    }
    /**
     * Search contacts by name, email, or company
     */
    async searchContacts(organizationId, searchQuery) {
        try {
            const { data, error } = await supabase
                .from('sales_contacts')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('is_active', true)
                .textSearch('search_vector', searchQuery)
                .limit(20);
            if (error) {
                console.error('Error searching contacts:', error);
                return [];
            }
            return this.transformContacts(data || []);
        }
        catch (error) {
            console.error('Error in searchContacts:', error);
            return [];
        }
    }
    /**
     * Get contacts with upcoming important dates
     */
    async getUpcomingImportantDates(organizationId, days = 30) {
        try {
            const today = new Date();
            const futureDate = new Date();
            futureDate.setDate(today.getDate() + days);
            const { data, error } = await supabase
                .from('sales_contacts')
                .select('id, full_name, birthday, anniversary, last_contact_date')
                .eq('organization_id', organizationId)
                .eq('is_active', true)
                .or(`birthday.gte.${today.toISOString()},birthday.lte.${futureDate.toISOString()},anniversary.gte.${today.toISOString()},anniversary.lte.${futureDate.toISOString()}`);
            if (error) {
                console.error('Error fetching important dates:', error);
                return [];
            }
            // Transform to important dates format
            const importantDates = [];
            data?.forEach(contact => {
                if (contact.birthday) {
                    importantDates.push({
                        contactId: contact.id,
                        contactName: contact.full_name,
                        dateType: 'birthday',
                        date: new Date(contact.birthday)
                    });
                }
                if (contact.anniversary) {
                    importantDates.push({
                        contactId: contact.id,
                        contactName: contact.full_name,
                        dateType: 'anniversary',
                        date: new Date(contact.anniversary)
                    });
                }
            });
            return importantDates.sort((a, b) => a.date.getTime() - b.date.getTime());
        }
        catch (error) {
            console.error('Error in getUpcomingImportantDates:', error);
            return [];
        }
    }
    /**
     * Get upcoming important dates with communication context
     * Shows "Last mail 5 months ago" type context for each date
     */
    async getImportantDatesWithContext(organizationId, days = 30) {
        try {
            // Get basic important dates
            const basicDates = await this.getUpcomingImportantDates(organizationId, days);
            // Enhance each date with communication context
            const datesWithContext = await Promise.all(basicDates.map(async (dateItem) => {
                // Get last activity for this contact
                const lastActivity = await salesActivitiesService.getLastContactActivity(organizationId, dateItem.contactId);
                const lastContactDate = lastActivity?.createdAt;
                const daysSince = lastContactDate ? getDaysSince(lastContactDate) : null;
                const contextMessage = formatCommunicationGap(lastContactDate, lastActivity?.activityType);
                return {
                    id: `${dateItem.contactId}-${dateItem.dateType}`,
                    contactId: dateItem.contactId,
                    contactName: dateItem.contactName,
                    type: dateItem.dateType,
                    label: dateItem.dateType.charAt(0).toUpperCase() + dateItem.dateType.slice(1),
                    date: dateItem.date,
                    lastContactDate,
                    lastContactType: lastActivity?.activityType,
                    daysSinceContact: daysSince || undefined,
                    contextMessage
                };
            }));
            return datesWithContext;
        }
        catch (error) {
            console.error('Error in getImportantDatesWithContext:', error);
            return [];
        }
    }
    /**
     * Subscribe to real-time contact changes
     */
    subscribeToContacts(organizationId, callback) {
        return supabase
            .channel(`sales_contacts:${organizationId}`)
            .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'sales_contacts',
            filter: `organization_id=eq.${organizationId}`
        }, (payload) => {
            if (payload.new) {
                callback(this.transformContact(payload.new));
            }
        })
            .subscribe();
    }
    /**
     * Transform database row to SalesContact type
     */
    transformContact(row) {
        return {
            id: row.id,
            organizationId: row.organization_id,
            firstName: row.first_name,
            lastName: row.last_name,
            fullName: row.full_name,
            email: row.email,
            phone: row.phone,
            mobile: row.mobile,
            companyName: row.company_name,
            jobTitle: row.job_title,
            website: row.website,
            contactType: row.contact_type,
            stage: row.stage,
            leadSource: row.lead_source,
            leadScore: row.lead_score || 0,
            streetAddress: row.street_address,
            city: row.city,
            state: row.state,
            postalCode: row.postal_code,
            country: row.country,
            linkedinUrl: row.linkedin_url,
            twitterUrl: row.twitter_url,
            facebookUrl: row.facebook_url,
            birthday: row.birthday ? new Date(row.birthday) : undefined,
            anniversary: row.anniversary ? new Date(row.anniversary) : undefined,
            lastContactDate: row.last_contact_date ? new Date(row.last_contact_date) : undefined,
            nextFollowUpDate: row.next_follow_up_date ? new Date(row.next_follow_up_date) : undefined,
            lifetimeValue: row.lifetime_value || 0,
            tags: row.tags || [],
            customFields: row.custom_fields || {},
            ownerId: row.owner_id,
            assignedTo: row.assigned_to,
            isActive: row.is_active,
            isStarred: row.is_starred || false,
            notes: row.notes,
            avatarUrl: row.avatar_url,
            createdBy: row.created_by,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at)
        };
    }
    /**
     * Transform multiple database rows
     */
    transformContacts(rows) {
        return rows.map(row => this.transformContact(row));
    }
}
// Export singleton instance
export const salesContactsService = new SalesContactsService();
